import React, { useState, useEffect, useRef, useContext } from 'react';
import URI from 'urijs';
import { get } from 'lodash';
import { windowWrapper } from '@clubmed/utils';
import { B2CAppContext } from 'Context/ReactAppContext';
import { handle401GraphqlErrorSeller, isSeller } from 'Utils/seller/sellerUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { ThreeSixty } from './ThreeSixty';
type State = {
  i18n: CMSContent | null | undefined;
  cxoMainContact: string | null | undefined;
  cxoLastResorts: Array<{
    id: string;
    name: string;
    category: Array<string>;
  }>;
  shouldRender: boolean;
};

export function ThreeSixtyContainer() {
  const context = useContext(B2CAppContext);
  const cxoMainContactIdRef = useRef<string>('');

  const [state, setState] = useState<State>(() => {
    const { cxo_primarycontact: cxoMainContactId, cxo_village: cxoLastResorts = [] } = new URI(
      windowWrapper.getLocation() || '',
    ).search(true);
    // when there is always one village it's not an array so we need to force it
    const arrayCxoLastResorts = Array.prototype.concat(cxoLastResorts);
    cxoMainContactIdRef.current = cxoMainContactId;

    return {
      i18n: null,
      cxoMainContact: null,
      cxoLastResorts: arrayCxoLastResorts.map((resort) => {
        const [resortId, roomCategories] = resort.split(' ');
        return {
          id: resortId,
          name: resortId,
          category: roomCategories.split(','),
        };
      }),
      shouldRender: isSeller(context) && cxoMainContactId && cxoLastResorts.length > 0,
    };
  });

  useEffect(() => {
    if (!state.shouldRender) {
      return;
    }

    let isCancelled = false;

    async function fetchData() {
      try {
        const { resorts, config, profile } = await graphQLClient.runNoCacheQuery(
          `{
        resorts(resortIds: ["${state.cxoLastResorts.map(({ id }) => id).join('","')}"]) {
          id
          name
        }
        config {
          booking {
            cxo {
              v360 {
                resortsAndRooms
              }
            }
          }
        }
        profile(userId: "${cxoMainContactIdRef.current}", throw401Error: true) {
          firstName
          lastName
        }
    }`,
          context,
        );

        if (isCancelled) {
          return;
        }

        setState((prev) => ({
          ...prev,
          i18n: config,
          cxoMainContact: `${profile.firstName} ${profile.lastName}`,
          cxoLastResorts: prev.cxoLastResorts.map((resort) => ({
            ...resort,
            name: get(
              resorts.find(({ id }) => id === resort.id),
              'name',
              resort.id,
            ),
          })),
          shouldRender: true,
        }));
      } catch (errors) {
        handle401GraphqlErrorSeller(errors, context);
      }
    }

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (!state.shouldRender || !state.cxoMainContact) {
    return null;
  }

  return (
    <ThreeSixty
      clientName={state.cxoMainContact}
      lastVisitsLabel={get(state.i18n, 'booking.cxo.v360.resortsAndRooms')}
      lastVisits={state.cxoLastResorts}
    />
  );
}
